import { Button, ButtonLoading, Tooltip } from '@tovala/component-library'
import { ComponentProps, ReactNode, useEffect, useState } from 'react'
import { useMobileAppConfiguration } from '@tovala/browser-apis-cdn'
import {
  useReferralTotals,
  useSendReferralEmail,
  UserV1,
} from '@tovala/browser-apis-combinedapi'

import {
  ErrorCodeMessageMapCombinedAPI,
  GoodErrorMessaging,
} from 'types/internal'
import { events, sourceIDs } from '../../analytics/events'
import { track } from 'utils/analytics'
import { formatCentsToDollars, hasCentsLeftOver } from 'utils/currency'
import { wrapWithContactSupportTeam } from 'utils/errors'

import { useToast } from 'contexts/toast'
import { useVariantByScreenSize } from 'hooks/variantByScreenSize'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import ErrorDisplay from 'components/common/ErrorDisplay'
import FormGroup from 'components/common/FormGroup'
import Input from 'components/common/Input'
import Modal, {
  ModalHeader,
  ModalPanel,
  ModalPanelStyle,
} from 'components/common/Modal'
import XTwitterIcon from 'components/common/icons/XTwitterIcon'
import FacebookIcon from 'components/common/icons/FacebookIcon'
import MailIcon from 'components/common/icons/MailIcon'
import BoxCheckIcon from 'components/common/icons/BoxCheckIcon'
import MailOpenIcon from 'components/common/icons/MailOpenIcon'
import ReceiveMoneyIcon from 'components/common/icons/ReceiveMoneyIcon'
import SendIcon from 'components/common/icons/SendIcon'
import SendMoney2Icon from 'components/common/icons/SendMoney2Icon'
import SendMoneyIcon from 'components/common/icons/SendMoneyIcon'
import { Link } from 'react-router-dom'

type ButtonSize = ComponentProps<typeof Button>['size']
type ButtonStyle = ComponentProps<typeof Button>['buttonStyle']

// found here: https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const SEND_REFERRAL_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't send invitations due to a technical issue on our end.",
  },
  EmailInvalid: {
    helpToFix: 'Please make sure the email you entered is valid and try again.',
    why: "We couldn't send an invitation because the email entered does not appear valid.",
  },
}

const HOW_IT_WORKS = [
  {
    description:
      'You can easily share by text or email, as well as copy the link',
    icon: <SendIcon />,
    title: "You send Tovala's best offer",
  },
  {
    description: 'Your link has a unique code that ties it to your account',
    icon: <MailOpenIcon />,
    title: 'Your friend opens the link',
  },
  {
    description:
      'Your friend goes through our checkout flow and purchases Tovala',
    icon: <BoxCheckIcon />,
    title: 'They sign up for Tovala',
  },
  {
    description: 'The Tovala Cash is automatically applied to your next order',
    icon: <SendMoney2Icon />,
    title: 'We give you free meals',
  },
]

const ReferralModal = ({
  onCloseModal,
  user,
}: {
  onCloseModal(): void
  user: UserV1
}) => {
  const { openToast } = useToast()

  const [copiedReferralLink, setCopiedReferralLink] = useState(false)
  const [copyError, setCopyError] = useState<GoodErrorMessaging | null>(null)
  const [email, setEmail] = useState({ error: '', value: '' })
  const [shareMessage, setShareMessage] = useState(
    "I'm referring you to Tovala!",
  )
  const [view, setView] = useState<'how-it-works' | 'referral-totals'>(
    'referral-totals',
  )

  const referralURL = `${window.location.origin}/referral/${user.referralCode.code}`

  const {
    data: mobileAppConfiguration,
    isError: isErrorMobileAppConfiguration,
    isLoading: isLoadingMobileAppConfiguration,
  } = useMobileAppConfiguration({
    refetchOnWindowFocus: false,
  })

  const { referral_offer_v2: referralOffer } = mobileAppConfiguration || {}

  useEffect(() => {
    if (mobileAppConfiguration) {
      const { share_dialog_messages } =
        mobileAppConfiguration.referral_offer || {}

      if (share_dialog_messages?.length) {
        const randomMessage =
          share_dialog_messages[
            Math.floor(Math.random() * share_dialog_messages.length)
          ]
        setShareMessage(randomMessage)
      }
    }
  }, [mobileAppConfiguration])

  const { data: referralTotals } = useReferralTotals({ userID: user.id })

  const [shareData, setShareData] = useState<ShareData | null>(null)

  useEffect(() => {
    if (navigator.canShare) {
      const shareData = {
        text: shareMessage,
        url: referralURL,
      }
      if (navigator.canShare(shareData)) {
        setShareData(shareData)
      }
    }
  }, [referralURL, shareMessage])

  const {
    error: sendReferralEmailError,
    isError: hasSendReferralEmailError,
    isPending: isSendingReferralEmail,
    mutate: sendReferralEmail,
  } = useSendReferralEmail({
    onSuccess: () => {
      track(events.USER_SENT_REFERRAL_BY_EMAIL)

      onCloseModal()

      openToast({
        heading: 'Email Sent',
        message: 'Your referral has been sent, thanks for sharing the love!',
        type: 'success',
      })
    },
  })

  const onCopyReferralLink = () => {
    setCopyError(null)

    navigator.clipboard.writeText(referralURL).then(
      () => {
        setCopiedReferralLink(true)
      },
      () => {
        setCopyError({
          helpToFix: 'Please try again or manually copy the referral link.',
          wayOut: wrapWithContactSupportTeam('If you need further assistance'),
          why: "We couldn't copy the referral link because your browser might not support this functionality.",
        })
      },
    )
  }

  const onSendReferral = () => {
    if (EMAIL_REGEX.test(email.value)) {
      sendReferralEmail({
        data: { receiverEmail: email.value },
        userID: user.id,
      })
    } else {
      setEmail((email) => {
        return { ...email, error: 'Please enter a valid email address.' }
      })
    }
  }

  useEffect(() => {
    let timeoutID: number | undefined

    if (copiedReferralLink) {
      timeoutID = window.setTimeout(() => {
        setCopiedReferralLink(false)
      }, 3000)
    }

    return () => {
      window.clearTimeout(timeoutID)
    }
  }, [copiedReferralLink])

  const [
    copyReferralLinkButtonSize,
    copyReferralLinkButtonStyle,
    modalPanelStyle,
  ] = useVariantByScreenSize<[ButtonSize, ButtonStyle, ModalPanelStyle]>(
    ['small', 'stroke', 'default'],
    {
      sm: ['fluid', 'link', 'fullHeight'],
    },
  )

  const copyReferralLinkButton = (
    <Button
      buttonStyle={copyReferralLinkButtonStyle}
      onClick={() => {
        onCopyReferralLink()
        track(events.TAPS_COPY_CODE, {
          source_id:
            view === 'how-it-works' ? sourceIDs.LEARN_MORE : sourceIDs.REFERRAL,
        })
      }}
      size={copyReferralLinkButtonSize}
      type="button"
    >
      <span className="sm:text-orange-1">Copy</span>
    </Button>
  )

  const heading = isLoadingMobileAppConfiguration
    ? ''
    : isErrorMobileAppConfiguration || !referralOffer?.title
      ? 'Share the ultimate dinnertime de-stressor.'
      : referralOffer.title

  return (
    <Modal onCloseModal={onCloseModal}>
      <ModalPanel panelStyle={modalPanelStyle}>
        <div className="flex h-full min-h-[620px] w-[517px] flex-col sm:min-h-0 sm:w-full">
          <ModalHeader
            onClickBack={
              view === 'how-it-works'
                ? () => setView('referral-totals')
                : undefined
            }
            onClickClose={onCloseModal}
          >
            Tovala Referral
          </ModalHeader>

          <div className="flex grow flex-col justify-between space-y-6 px-6 py-10 sm:px-4 sm:py-6">
            {view === 'how-it-works' ? (
              <div className="space-y-8 sm:space-y-4">
                <h1 className="whitespace-pre-wrap text-center text-k/32_105 font-semibold md:font-normal sm:text-left sm:text-k/26_110">
                  How it Works
                </h1>
                <div className="space-y-6 sm:space-y-4">
                  {HOW_IT_WORKS.map((item) => (
                    <div key={item.title} className="flex space-x-2">
                      <div className="size-8 text-orange-2">{item.icon}</div>
                      <div className="space-y-1">
                        <p className="text-k/18_120">{item.title}</p>
                        <p className="text-body-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : view === 'referral-totals' ? (
              <div className="space-y-6">
                <div className="text-center">
                  {referralOffer?.hero_image && (
                    <img
                      alt=""
                      className="mx-auto mb-6 hidden rounded-xl sm:block"
                      src={referralOffer.hero_image}
                    />
                  )}
                  <h1 className="mx-auto mb-4 max-w-[285px] whitespace-pre-wrap text-k/32_105 font-semibold">
                    {heading}
                  </h1>

                  {referralOffer?.subtitle && (
                    <p className="mx-auto mb-3 max-w-[342px] text-k/14_120 text-grey-8">
                      {referralOffer.subtitle}
                    </p>
                  )}

                  <div className="flex justify-center">
                    <Button
                      buttonStyle="link"
                      onClick={() => setView('how-it-works')}
                      size="auto"
                    >
                      <span className="text-k/16_125 text-orange-2">
                        Learn More
                        <span className="sr-only">
                          {' '}
                          about how referrals work
                        </span>
                      </span>
                    </Button>
                  </div>
                </div>

                {referralTotals && (
                  <div className="flex items-center justify-center space-x-3 text-k/16_125">
                    <ReferralTotalItem
                      icon={<ReceiveMoneyIcon />}
                      value={`${referralTotals.numberOfReferrals} Referred`}
                    />

                    <ReferralTotalItem
                      icon={<SendMoneyIcon />}
                      value={`${formatCentsToDollars(
                        referralTotals.totalAmountReferralsCents,
                        {
                          digits: hasCentsLeftOver(
                            referralTotals.totalAmountReferralsCents,
                          )
                            ? 2
                            : 0,
                        },
                      )} Earned`}
                    />
                  </div>
                )}
              </div>
            ) : null}

            <div className="space-y-3">
              <div className="space-y-2 sm:hidden">
                <div className="flex items-center space-x-4 sm:space-y-3">
                  <div className="grow sm:w-full">
                    <FormGroup error={email.error}>
                      <Input
                        hasError={!!email.error}
                        onChange={(e) =>
                          setEmail((email) => {
                            return { ...email, value: e.target.value }
                          })
                        }
                        placeholder="Friend's Email Address"
                        type="email"
                        value={email.value}
                      />
                    </FormGroup>
                  </div>

                  <div className="w-32 self-stretch sm:h-9 sm:w-28 sm:text-k/14_120">
                    <ButtonLoading
                      isLoading={isSendingReferralEmail}
                      onClick={() => {
                        onSendReferral()
                      }}
                      size="fluid"
                    >
                      Invite
                    </ButtonLoading>
                  </div>
                </div>

                {hasSendReferralEmailError && (
                  <APIErrorDisplay
                    error={sendReferralEmailError}
                    errorCodeMessageMap={SEND_REFERRAL_ERRORS}
                  />
                )}
              </div>
              {view === 'referral-totals' && (
                <p className="text-center text-k/14_120 text-grey-8">
                  <span className="sm:hidden">
                    Invite your friends by email or on social.
                  </span>{' '}
                  By inviting a referral, you agree to Tovala's{' '}
                  <Link
                    className="underline"
                    target="_blank"
                    to="/legal/terms-of-service"
                  >
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link
                    className="underline"
                    target="_blank"
                    to="/legal/promotional-terms"
                  >
                    Promotional Terms
                  </Link>
                  .
                </p>
              )}

              {view === 'referral-totals' && (
                <div className="sm:hidden">
                  <SocialShareButtons
                    referralUrl={referralURL}
                    shareMessage={shareMessage}
                  />
                </div>
              )}

              {referralURL && (
                <div className="space-y-4">
                  <h2 className="hidden text-center text-k/20_110 sm:block">
                    Share Your Link
                  </h2>
                  <div className="flex items-center justify-center space-x-4 sm:justify-between sm:space-x-3 sm:rounded-xl sm:bg-grey-2 sm:p-3">
                    <a
                      className="text-k/14_120 text-orange-1 underline sm:truncate sm:text-ellipsis sm:text-k/14_120 sm:text-black"
                      href={referralURL}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {referralURL}
                    </a>

                    {copiedReferralLink ? (
                      <Tooltip
                        alwaysShow
                        trigger={<div>{copyReferralLinkButton}</div>}
                      >
                        Copied
                      </Tooltip>
                    ) : (
                      <div>{copyReferralLinkButton}</div>
                    )}
                  </div>

                  {copyError && <ErrorDisplay {...copyError} />}

                  {shareData && (
                    <div className="hidden h-12 sm:block">
                      <Button
                        buttonStyle="orange"
                        onClick={() => {
                          navigator
                            .share(shareData)
                            .then(() => {
                              track(events.TAPS_SHARE_CODE, {
                                source_id:
                                  view === 'how-it-works'
                                    ? sourceIDs.LEARN_MORE
                                    : sourceIDs.REFERRAL,
                              })
                            })
                            .catch((error: unknown) => {
                              if (
                                error instanceof Error &&
                                error.name === 'AbortError'
                              ) {
                                return
                              }

                              setCopyError({
                                helpToFix:
                                  'Please try again or copy the referral link to manually share.',
                                wayOut: wrapWithContactSupportTeam(
                                  'If you need further assistance',
                                ),
                                why: "We couldn't share the referral link because your browser might not support this functionality.",
                              })
                            })
                        }}
                        size="fluid"
                      >
                        Share
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalPanel>
    </Modal>
  )
}

export default ReferralModal

const ReferralTotalItem = ({
  icon,
  value,
}: {
  icon: ReactNode
  value: string
}) => {
  return (
    <div className="flex w-[174px] flex-col items-center justify-center space-y-2 rounded-lg bg-grey-2 py-4">
      <div className="size-8">{icon}</div>
      <span>{value}</span>
    </div>
  )
}

function SocialShareButtons({
  referralUrl,
  shareMessage,
}: {
  referralUrl: string
  shareMessage: string
}) {
  return (
    <div className="flex items-center justify-center space-x-6 sm:space-x-4">
      <Email referralUrl={referralUrl} shareMessage={shareMessage} />

      <Facebook referralUrl={referralUrl} />

      <Twitter referralUrl={referralUrl} shareMessage={shareMessage} />
    </div>
  )
}

const Email = ({
  referralUrl,
  shareMessage,
}: {
  referralUrl: string
  shareMessage: string
}) => {
  return (
    <a
      href={`mailto:?body=${shareMessage} ${referralUrl}`}
      onClick={() => track(events.TAPS_SHARE_CODE)}
    >
      <span className="block w-6">
        <MailIcon />
      </span>
    </a>
  )
}

// This Facebook share link only allows a URL
// Could replace it with Share Dialog if we have a Facebook App ID we can use
// https://developers.facebook.com/docs/sharing/reference/share-dialog
const Facebook = ({ referralUrl }: { referralUrl: string }) => {
  return (
    <a
      href={`https://www.facebook.com/sharer/sharer.php?u=${referralUrl}`}
      onClick={() => track(events.TAPS_SHARE_CODE)}
      rel="noreferrer"
      target="_blank"
      title="Share on Facebook"
    >
      <span className="block w-6">
        <FacebookIcon />
      </span>
    </a>
  )
}

const Twitter = ({
  referralUrl,
  shareMessage,
}: {
  referralUrl: string
  shareMessage: string
}) => {
  return (
    <a
      href={`https://twitter.com/share?text=${shareMessage}&url=${referralUrl}`}
      onClick={() => track(events.TAPS_SHARE_CODE)}
      rel="noreferrer"
      target="_blank"
      title="Share on Twitter"
    >
      <span className="block w-6">
        <XTwitterIcon />
      </span>
    </a>
  )
}
